// Version-related type definitions

export interface VersionDetails {
  id: string;
  version: string;
  status: "Draft" | "Preview" | "Published" | "Archived";
  publishedDate: string;
  createdDate: string;
  archivedDate?: string;
  description: string;
  releaseNotes?: string;
  subscribers: number;
  isActive: boolean;
  workflowVersions?: {
    name: string;
    previousVersion: string;
    currentVersion: string;
    isUpdated: boolean;
    isDeleted: boolean;
  }[];
  connections: ConnectionDefinitionInfo[];
  datastoreNames: string[];
  author: {
    name: string;
    email: string;
  };
}

export interface VersionCreationData {
  version: string;
  versionType: "patch" | "minor" | "major" | "custom";
  action: "discard" | "keep" | "publish";
  description: string;
  releaseNotes: string;
}

export interface PublishData {
  versionId: string;
  publishType: "immediate" | "scheduled";
  scheduledDate?: string;
  scheduledTime?: string;
  notifications: {
    emailSubscribers: boolean;
    includeReleaseNotes: boolean;
    customMessage?: string;
  };
  rolloutStrategy: "all" | "gradual";
  rolloutPercentage?: number;
}

export interface PublishableVersion {
  id: string;
  version: string;
  description: string;
  status: string;
  subscribers: number;
}

export interface Subscriber {
  id: string;
  name: string;
  email: string;
  subscribedDate: string;
  status: "Active" | "Paused" | "Inactive";
}

export interface WorkflowVersionSnapshotInfo {
  versionId: string;
  versionLabel: string;
  /** Name of the workflow this snapshot belongs to. Empty when the workflow was deleted. */
  workflowName: string;
  /** True when the referenced workflow has been deleted since this (non-live) version was created. */
  isDeleted: boolean;
}

export interface ConnectionDefinitionInfo {
  name: string;
  type: string;
}

// API response type matching backend ProductVersionResponse
export interface ProductVersionSummary {
  id: string;
  versionNumber: string;
  status: "Draft" | "Preview" | "Published" | "Archived";
  description: string;
  releaseNotes?: string;
  createdDate: string;
  publishedDate?: string;
  archivedDate?: string;
  workflowVersionSnapshots: Record<string, WorkflowVersionSnapshotInfo>;
  /** Connection slots this version snapshotted from the master's workflows. */
  connections: ConnectionDefinitionInfo[];
  /** Names of datastores snapshotted by this version. Values intentionally excluded. */
  datastoreNames: string[];
  /** Orgs allowed to consume this version while it is in Preview status. Empty otherwise. */
  previewSubscriberOrgIds: string[];
  /** Orgs currently consuming this version: active subscriptions for Published, preview-org count for Preview, 0 otherwise. */
  subscriberCount: number;
}

// Maps API ProductVersionSummary → UI VersionDetails for modal compatibility
export function toVersionDetails(
  summary: ProductVersionSummary,
  workflows: { id: string; name: string }[] = [],
): VersionDetails {
  const workflowVersions = Object.entries(summary.workflowVersionSnapshots).map(
    ([workflowId, snapshot]) => {
      // Prefer the name the backend resolved from the snapshot; fall back to the master list, then to a
      // friendly "(deleted workflow)" label — never a raw GUID. A deleted workflow keeps its name when
      // the version captured it at creation (WorkflowSnapshotMeta); older versions have none, so show
      // the generic label. The backend flags IsDeleted on a snapshot whose workflow was deleted.
      const masterName = workflows.find((w) => w.id === workflowId)?.name;
      const name = snapshot.isDeleted
        ? snapshot.workflowName || "(deleted workflow)"
        : snapshot.workflowName || masterName || workflowId;
      return {
        name,
        previousVersion: "",
        // The version label is the one this release shipped. For a deleted workflow the backend serves
        // the label captured at creation; only older (pre-capture) versions leave it blank → "—".
        currentVersion: snapshot.versionLabel || "—",
        isUpdated: true,
        isDeleted: snapshot.isDeleted,
      };
    },
  );

  return {
    id: summary.id,
    version: summary.versionNumber,
    status: summary.status,
    publishedDate: summary.publishedDate ?? "",
    createdDate: summary.createdDate,
    archivedDate: summary.archivedDate,
    description: summary.description,
    releaseNotes: summary.releaseNotes,
    subscribers: summary.subscriberCount ?? 0,
    isActive: summary.status === "Published",
    workflowVersions,
    connections: summary.connections ?? [],
    datastoreNames: summary.datastoreNames ?? [],
    author: { name: "", email: "" },
  };
}
